export type User = {
  id: string;
  email: string;
  display_name: string;
  is_admin: boolean;
};

export type Profile = {
  full_name: string;
  email: string;
  phone: string;
  location: string;
  linkedin_url: string;
  website_url: string;
  referees: Referee[];
  body_md: string;
  version: number;
  header_line: string;
};

export type Referee = {
  name: string;
  position: string;
  phone: string;
  email: string;
};

export type ContactUpdate = Omit<Profile, "body_md" | "version" | "header_line" | "referees">;

export type HealthResponse = {
  status: string;
  checks: Record<string, string>;
};

export type ApplicationStatus =
  | "reviewing"
  | "applied"
  | "screening"
  | "interviewing"
  | "offer"
  | "rejected"
  | "withdrawn";

export const APPLICATION_STATUSES: ApplicationStatus[] = [
  "reviewing",
  "applied",
  "screening",
  "interviewing",
  "offer",
  "rejected",
  "withdrawn",
];

export type ArtifactKind =
  | "job_ad_notes"
  | "fitness_report"
  | "resume_md"
  | "cover_letter_md"
  | "resume_pdf"
  | "cover_letter_pdf";

export type ArtifactState = "fresh" | "stale" | "failed";

export type Artifact = {
  id: string;
  kind: ArtifactKind;
  revision: number;
  state: ArtifactState;
  edited_by_user: boolean;
  has_content: boolean;
  error_log: string | null;
};

export type ArtifactContent = Artifact & { content_text: string | null };

export type Application = {
  id: string;
  company: string;
  role: string;
  location: string;
  employment_type: string;
  salary_text: string;
  seniority: string;
  source_url: string;
  fit_score: number | null;
  status: ApplicationStatus;
  notes: string;
  board_order: number;
  archived: boolean;
  created_at: string;
  title: string;
};

export type ApplicationDetail = Application & {
  job_ad_md: string;
  artifacts: Artifact[];
  active_job_id: string | null;
};

export type StepStatus = "pending" | "running" | "done" | "skipped" | "failed";
export type JobStatus = "queued" | "running" | "done" | "failed" | "cancelled";

export type JobStep = {
  name: ArtifactKind;
  label: string;
  ordinal: number;
  status: StepStatus;
  error: string | null;
};

export type Job = {
  id: string;
  application_id: string;
  status: JobStatus;
  error: string | null;
  steps: JobStep[];
};

export const ARTIFACT_LABELS: Record<ArtifactKind, string> = {
  job_ad_notes: "Job ad notes",
  fitness_report: "Fitness report",
  resume_md: "Resume",
  cover_letter_md: "Cover letter",
  resume_pdf: "Resume PDF",
  cover_letter_pdf: "Cover letter PDF",
};
